import React, { useEffect, useMemo, useState } from "react"

import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { Layers, Workflow } from "lucide-react"
import { toast } from "sonner"

import type { JobPostingStage } from "@/components/applicants/DocumentsList"
import { stagesConfig } from "@/components/job-postings/KanbanStageConfig"
import SheriffTypeChangeWarningDialog from "@/components/people/SheriffTypeChangeWarningDialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  type ActiveEngagements,
  type UpcomingShift,
  useFetchActiveEngagements,
} from "@/hooks/useActiveEngagements"
import { useFetchCompletionShiftConflicts } from "@/hooks/useMismatchedUpcomingShifts"
import { useUpdateApplicationStage } from "@/hooks/useUpdateApplicationStage"

import RejectWarningDialog from "../applications/RejectWarningDialog"

interface MoveStageDialogProps {
  isOpen: boolean
  onClose: () => void
  currentStage: string
  currentSegment: string | null
  applicantName: string
  applicationId: string
  /** The linked person's `people.id` UUID (applications.person_id), used to check active engagements before rejecting. */
  personId?: string
  allowedStages?: JobPostingStage[]
}

const MoveStageDialog = ({
  isOpen,
  onClose,
  currentStage,
  currentSegment,
  applicantName,
  applicationId,
  personId,
  allowedStages = [],
}: MoveStageDialogProps) => {
  const [selectedStage, setSelectedStage] = useState<string>(currentStage)
  const [selectedSegment, setSelectedSegment] = useState<string | null>(currentSegment)
  const [pendingReject, setPendingReject] = useState<{ engagements: ActiveEngagements }>(undefined)
  const [upcomingShifts, setUpcomingShifts] = useState<UpcomingShift[] | null>(null)
  const { mutate: updateStage } = useUpdateApplicationStage()
  const fetchActiveEngagements = useFetchActiveEngagements()
  const fetchCompletionShiftConflicts = useFetchCompletionShiftConflicts()
  const { mutateAsync: checkActiveEngagements, isPending: isCheckingEngagements } = useMutation({
    mutationFn: fetchActiveEngagements,
  })
  const { mutateAsync: checkCompletionShiftConflicts, isPending: isCheckingShifts } = useMutation({
    mutationFn: fetchCompletionShiftConflicts,
  })

  useEffect(() => {
    if (isOpen) {
      setSelectedStage(currentStage)
      setSelectedSegment(currentSegment)
    }
  }, [isOpen, currentStage, currentSegment])

  // Resolve segment options based on allowedStages; only segments present in allowedStages are valid
  const segmentOptions = useMemo(() => {
    const globalStage = stagesConfig.find(
      (s) => s.stage === selectedStage || s.id === selectedStage,
    )
    const globalSegs = globalStage?.segments ?? []

    if (allowedStages.length > 0) {
      const allowedStage = allowedStages.find((s) => s.id === selectedStage) || null

      // If allowed stage explicitly lists segments, filter global segments by those ids
      if (allowedStage?.segments && allowedStage.segments.length > 0) {
        const allowedIds = new Set(allowedStage.segments.map((seg) => seg.id))
        return globalSegs.filter((seg) => allowedIds.has(seg.id))
      }
      // If no segments listed on allowed stage, treat as unrestricted -> use global segments
    }

    // Fallback to global config when no allowedStages or no allowed segments specified
    return globalSegs
  }, [allowedStages, selectedStage])

  // Whenever the selected stage changes, pick first allowed segment if stage changed; otherwise retain current segment
  useEffect(() => {
    if (selectedStage !== currentStage) {
      const first = segmentOptions.length > 0 ? segmentOptions[0].id : null
      setSelectedSegment(first)
    } else {
      setSelectedSegment(currentSegment)
    }
  }, [selectedStage, currentStage, currentSegment, segmentOptions])

  const submitStageChange = () => {
    Sentry.addBreadcrumb({
      category: "ui",
      level: "info",
      message: "Stage transition",
      data: { applicationId, toStage: selectedStage },
    })
    updateStage({
      id: applicationId,
      stage: selectedStage,
      segment: selectedSegment ?? undefined,
    })
    toast.success("Stage Updated", {
      description: `${applicantName} moved to ${selectedStage}`,
    })
    setPendingReject(undefined)
    setUpcomingShifts(null)
    onClose()
    setSelectedStage("")
    setSelectedSegment(null)
  }

  const handleMoveStage = async () => {
    if (!selectedStage) return

    // For rejections, warn if the person still has open timesheets / claimed shifts.
    if (selectedStage === "rejected") {
      try {
        const engagements = await checkActiveEngagements(personId)
        if (engagements.hasActiveEngagements) {
          setPendingReject({ engagements })
          return
        }
      } catch (_error) {
        toast.warning("Could not check active engagements", {
          description: "Proceeding with the rejection without the active work check.",
        })
      }
    }

    if (selectedStage === "complete") {
      try {
        const shifts = await checkCompletionShiftConflicts(applicationId)
        if (shifts.length > 0) {
          setUpcomingShifts(shifts)
          return
        }
      } catch {
        toast.warning("Could not check upcoming shifts", {
          description:
            "Completing anyway. Upcoming shifts that don't match the job's sheriff type will be cancelled.",
        })
      }
    }

    submitStageChange()
  }

  const handleClose = () => {
    onClose()
    setSelectedStage("")
    setSelectedSegment(null)
  }

  // Determine if a stage is allowed for this job posting
  const isStageAllowed = (stage: string) => {
    // Always allow rejected and hold stages
    const isSpecialStage = stage === "rejected" || stage === "hold" || stage === "complete"
    return (
      isSpecialStage ||
      allowedStages.length === 0 || // If no allowedStages provided, allow all
      allowedStages.some((allowedStage) => allowedStage.id === stage)
    )
  }

  // Filter available stages by allowedStages
  const availableStages = stagesConfig.filter((stage) => {
    // Don't include current stage
    return isStageAllowed(stage.stage)
  })

  const isSelectedSegmentAllowed = useMemo(() => {
    if (segmentOptions.length === 0) return true // no segment requirement
    if (!selectedSegment) return false
    return segmentOptions.some((s) => s.id === selectedSegment)
  }, [segmentOptions, selectedSegment])
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move Application Stage</DialogTitle>
          <DialogDescription>
            Select the new stage for {applicantName}'s application.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Layers className="h-4 w-4" />
              Stage
            </Label>
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger>
                <SelectValue placeholder="Select a stage..." />
              </SelectTrigger>
              <SelectContent>
                {availableStages.map((stage) => {
                  const isCurrentStage = stage.stage === currentStage
                  return (
                    <SelectItem key={stage.id} value={stage.stage} disabled={isCurrentStage}>
                      <div className="flex items-center gap-2">
                        <stage.icon className="h-4 w-4" style={{ color: stage.color }} />
                        {stage.label} {isCurrentStage && "(Current)"}
                      </div>
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          {segmentOptions.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Workflow className="h-4 w-4" />
                Segment
              </Label>
              <Select value={selectedSegment ?? ""} onValueChange={(v) => setSelectedSegment(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a segment..." />
                </SelectTrigger>
                <SelectContent>
                  {segmentOptions.map((seg) => (
                    <SelectItem key={seg.id} value={seg.id}>
                      {seg.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleMoveStage}
            disabled={
              !selectedStage ||
              !isSelectedSegmentAllowed ||
              isCheckingEngagements ||
              isCheckingShifts
            }
          >
            Move Stage
          </Button>
        </DialogFooter>
      </DialogContent>

      {pendingReject && (
        <RejectWarningDialog
          open
          onOpenChange={(open) => {
            if (!open) setPendingReject(undefined)
          }}
          applicantName={applicantName}
          engagements={pendingReject.engagements}
          onConfirm={submitStageChange}
        />
      )}

      {upcomingShifts && (
        <SheriffTypeChangeWarningDialog
          open
          onOpenChange={(open) => {
            if (!open) setUpcomingShifts(null)
          }}
          trigger="application-complete"
          personName={applicantName}
          upcomingShifts={upcomingShifts}
          onConfirm={submitStageChange}
        />
      )}
    </Dialog>
  )
}

export default MoveStageDialog
